// Mock data and formatting helpers for the Analytics page.
export const tabs = ['Sales', 'Stock Forecast', 'Product Performance', 'Recurring Revenue', 'Retention Health', 'Wine Journey', 'Cancellation Timing'] as const;
export type Tab = (typeof tabs)[number];
export const timeHorizons = ['Next 7 Days', 'Next 30 Days', 'Next 60 Days', 'Next 90 Days'] as const;
export type TimeHorizon = (typeof timeHorizons)[number];
export interface ProductData {
  name: string;
  variant: string;
  sku: string;
  baseRequired: number;
  stock: number;
}
export const baseProducts: ProductData[] = [{
  name: '2023 Reserve Cabernet',
  variant: '750ml',
  sku: '23RCAB-750',
  baseRequired: 89,
  stock: 45
}, {
  name: '2024 Sparkling Brut',
  variant: '750ml',
  sku: '24SPB-750',
  baseRequired: 34,
  stock: 0
}, {
  name: '2023 Rosé',
  variant: '750ml',
  sku: '23RSE-750',
  baseRequired: 42,
  stock: 15
}, {
  name: '2025 Chardonnay',
  variant: '750ml',
  sku: '25CHD3-750',
  baseRequired: 120,
  stock: 130
}, {
  name: '2024 Sauvignon Blanc',
  variant: '750ml',
  sku: '24SB2-750',
  baseRequired: 95,
  stock: 100
}, {
  name: '2024 Shiraz',
  variant: '750ml',
  sku: '24SHZ9-750',
  baseRequired: 156,
  stock: 200
}, {
  name: '2024 Pinot Noir',
  variant: '750ml',
  sku: '24PN5-750',
  baseRequired: 67,
  stock: 180
}, {
  name: '2023 Merlot',
  variant: '750ml',
  sku: '23MRL-750',
  baseRequired: 78,
  stock: 250
}];
// Product Performance Data Types & Constants
export type SalesMode = 'subscription' | 'all';
export interface PerfProduct {
  name: string;
  sku: string;
  category: string;
  unitsSold: number;
  revenue: number;
  avgPrice: number;
  trend: number | null; // percentage, positive = up, negative = down, null = no trend
  activeSubs: number | null; // null for non-subscription products
  allUnits: number;
  allRevenue: number;
}
export const perfProducts: PerfProduct[] = [{
  name: '2024 Shiraz',
  sku: '24SHZ9',
  category: 'Red',
  unitsSold: 156,
  revenue: 4680,
  avgPrice: 30,
  trend: 12,
  activeSubs: 89,
  allUnits: 420,
  allRevenue: 12600
}, {
  name: '2025 Chardonnay',
  sku: '25CHD3',
  category: 'White',
  unitsSold: 120,
  revenue: 3600,
  avgPrice: 30,
  trend: 8,
  activeSubs: 72,
  allUnits: 310,
  allRevenue: 9300
}, {
  name: '2024 Sauvignon Blanc',
  sku: '24SB2',
  category: 'White',
  unitsSold: 95,
  revenue: 2375,
  avgPrice: 25,
  trend: -3,
  activeSubs: 58,
  allUnits: 260,
  allRevenue: 6500
}, {
  name: '2023 Reserve Cabernet',
  sku: '23RCAB',
  category: 'Red',
  unitsSold: 89,
  revenue: 3560,
  avgPrice: 40,
  trend: 22,
  activeSubs: 45,
  allUnits: 195,
  allRevenue: 7800
}, {
  name: '2023 Merlot',
  sku: '23MRL',
  category: 'Red',
  unitsSold: 78,
  revenue: 2340,
  avgPrice: 30,
  trend: -8,
  activeSubs: 38,
  allUnits: 210,
  allRevenue: 6300
}, {
  name: '2024 Pinot Noir',
  sku: '24PN5',
  category: 'Red',
  unitsSold: 67,
  revenue: 2345,
  avgPrice: 35,
  trend: -5,
  activeSubs: 41,
  allUnits: 180,
  allRevenue: 6300
}, {
  name: '2023 Rosé',
  sku: '23RSE',
  category: 'Rosé',
  unitsSold: 42,
  revenue: 1050,
  avgPrice: 25,
  trend: 15,
  activeSubs: 28,
  allUnits: 115,
  allRevenue: 2875
}, {
  name: '2024 Sparkling Brut',
  sku: '24SPB',
  category: 'Sparkling',
  unitsSold: 34,
  revenue: 1190,
  avgPrice: 35,
  trend: 45,
  activeSubs: 22,
  allUnits: 88,
  allRevenue: 3080
}];
export const allSalesOnlyProducts: PerfProduct[] = [{
  name: "Oliver's Olive Oil",
  sku: 'OLIVEOIL',
  category: 'Pantry',
  unitsSold: 0,
  revenue: 0,
  avgPrice: 29.1,
  trend: null,
  activeSubs: null,
  allUnits: 210,
  allRevenue: 6111
}, {
  name: 'Gift Voucher $50',
  sku: 'GIFT50',
  category: 'Gifts',
  unitsSold: 0,
  revenue: 0,
  avgPrice: 50,
  trend: null,
  activeSubs: null,
  allUnits: 45,
  allRevenue: 2250
}];
export const subscriptionCategories = [{
  name: 'All Products',
  count: null
}, {
  name: 'Red',
  count: 16
}, {
  name: 'White',
  count: 12
}, {
  name: 'Sparkling',
  count: 4
}, {
  name: 'Rosé',
  count: 3
}, {
  name: 'Fortified',
  count: 2
}, {
  name: 'Mixed Packs',
  count: 3
}, {
  name: 'Accessories',
  count: 2
}];
export const allSalesCategories = [...subscriptionCategories, {
  name: 'Gifts',
  count: 16
}, {
  name: 'Pantry',
  count: 4
}];
// Recurring Revenue Data Types & Constants
export const revenueHorizons = ['Next 7 Days', 'Next 30 Days', 'Next 3 Months', 'Next 12 Months'] as const;
export type RevenueHorizon = (typeof revenueHorizons)[number];
export interface RevenueProduct {
  name: string;
  subscriptionCount: number;
  baseForecastUnits: number;
  baseForecastRevenue: number;
  avgRecurring: number;
  avgLTV: number;
}
export const retentionScoreData = [{
  month: 'Jan',
  score: 65
}, {
  month: 'Feb',
  score: 67
}, {
  month: 'Mar',
  score: 68
}, {
  month: 'Apr',
  score: 70
}, {
  month: 'May',
  score: 71
}, {
  month: 'Jun',
  score: 72
}, {
  month: 'Jul',
  score: 73
}, {
  month: 'Aug',
  score: 74
}, {
  month: 'Sep',
  score: 74
}, {
  month: 'Oct',
  score: 76
}, {
  month: 'Nov',
  score: 77
}, {
  month: 'Dec',
  score: 78
}];
export const tenureDistributionData = [{
  range: '0-3m',
  count: 28
}, {
  range: '3-6m',
  count: 42
}, {
  range: '6-12m',
  count: 95
}, {
  range: '1-2y',
  count: 88
}, {
  range: '2-3y',
  count: 55
}, {
  range: '3+y',
  count: 42
}];
export const waterfallChartData = [{
  name: 'Active Start',
  placeholder: 328,
  value: 14,
  color: '#0EA5E9',
  label: '342'
}, {
  name: 'New',
  placeholder: 342,
  value: 18,
  color: '#10B981',
  label: '+18'
}, {
  name: 'Reactivated',
  placeholder: 360,
  value: 4,
  color: '#10B981',
  label: '+4'
}, {
  name: 'Cancelled',
  placeholder: 350,
  value: 14,
  color: '#EF4444',
  label: '-14'
}, {
  name: 'Expired',
  placeholder: 348,
  value: 2,
  color: '#EF4444',
  label: '-2'
}, {
  name: 'Active End',
  placeholder: 328,
  value: 20,
  color: '#0EA5E9',
  label: '348'
}];
export const retentionCurveData = [{
  month: 'M0',
  club: 100,
  industry: 100
}, {
  month: 'M3',
  club: 92,
  industry: 85
}, {
  month: 'M6',
  club: 85,
  industry: 70
}, {
  month: 'M9',
  club: 78,
  industry: 62
}, {
  month: 'M12',
  club: 72,
  industry: 55
}, {
  month: 'M15',
  club: 68,
  industry: 48
}, {
  month: 'M18',
  club: 64,
  industry: 44
}, {
  month: 'M21',
  club: 61,
  industry: 40
}, {
  month: 'M24',
  club: 58,
  industry: 38
}];
// Cancellation Timing Data
export const cancellationsByCycle = [{
  label: '1',
  count: 6,
  remindersSent: 1,
  remindersOpened: 0.4
}, {
  label: '2',
  count: 19,
  remindersSent: 1.8,
  remindersOpened: 0.6
}, {
  label: '3',
  count: 11,
  remindersSent: 2.4,
  remindersOpened: 0.9
}, {
  label: '4',
  count: 5,
  remindersSent: 3.6,
  remindersOpened: 2.1
}, {
  label: '5',
  count: 4,
  remindersSent: 4.2,
  remindersOpened: 2.8
}, {
  label: '6+',
  count: 2,
  remindersSent: 5.1,
  remindersOpened: 3.4
}];
export const cancellationsByMonth = [{
  label: '1',
  count: 4,
  remindersSent: 1,
  remindersOpened: 0.4
}, {
  label: '2',
  count: 9,
  remindersSent: 1.8,
  remindersOpened: 0.6
}, {
  label: '3',
  count: 14,
  remindersSent: 2.4,
  remindersOpened: 0.9
}, {
  label: '4',
  count: 8,
  remindersSent: 3.6,
  remindersOpened: 2.1
}, {
  label: '5',
  count: 7,
  remindersSent: 4.2,
  remindersOpened: 2.8
}, {
  label: '6+',
  count: 5,
  remindersSent: 5.1,
  remindersOpened: 3.4
}];
export const cancellationTimingSummary = [{
  label: 'Median cycles to cancel',
  value: '2'
}, {
  label: 'Cancelled within 7 days of billing',
  value: '61%'
}, {
  label: 'Cancelled mid-cycle',
  value: '39%'
}];
// Wine Journey Data
export const productLifecycleData = [{
  name: '2024 Rosé',
  tenure: 2,
  repeatRate: 15,
  units: 115,
  color: '#F472B6'
}, {
  name: '2024 Sauv Blanc',
  tenure: 3,
  repeatRate: 25,
  units: 260,
  color: '#34D399'
}, {
  name: '2023 Shiraz',
  tenure: 8,
  repeatRate: 65,
  units: 420,
  color: '#0EA5E9'
}, {
  name: '2023 Reserve Cab',
  tenure: 14,
  repeatRate: 72,
  units: 195,
  color: '#8B5CF6'
}, {
  name: '2024 Pinot Noir',
  tenure: 6,
  repeatRate: 45,
  units: 180,
  color: '#F59E0B'
}, {
  name: '2023 Sparkling',
  tenure: 1,
  repeatRate: 8,
  units: 88,
  color: '#94A3B8'
}];
export const swapDropData = [{
  name: '2024 Pinot Noir',
  count: 34,
  type: 'in'
}, {
  name: '2023 Shiraz',
  count: 28,
  type: 'in'
}, {
  name: '2024 Rosé',
  count: 22,
  type: 'out'
}, {
  name: '2023 Merlot',
  count: 18,
  type: 'out'
}, {
  name: '2024 Sauv Blanc',
  count: 15,
  type: 'in'
}, {
  name: '2023 Sparkling',
  count: 12,
  type: 'out'
}];
export const varietalDiversityData = [{
  month: 'Jan',
  shiraz: 32,
  cabernet: 22,
  pinotNoir: 12,
  chardonnay: 10,
  sauvBlanc: 8,
  rose: 6,
  sparkling: 5,
  other: 5
}, {
  month: 'Feb',
  shiraz: 30,
  cabernet: 21,
  pinotNoir: 13,
  chardonnay: 11,
  sauvBlanc: 9,
  rose: 6,
  sparkling: 5,
  other: 5
}, {
  month: 'Mar',
  shiraz: 29,
  cabernet: 20,
  pinotNoir: 14,
  chardonnay: 11,
  sauvBlanc: 9,
  rose: 7,
  sparkling: 5,
  other: 5
}, {
  month: 'Apr',
  shiraz: 28,
  cabernet: 19,
  pinotNoir: 14,
  chardonnay: 12,
  sauvBlanc: 10,
  rose: 7,
  sparkling: 5,
  other: 5
}, {
  month: 'May',
  shiraz: 27,
  cabernet: 18,
  pinotNoir: 15,
  chardonnay: 12,
  sauvBlanc: 10,
  rose: 8,
  sparkling: 5,
  other: 5
}, {
  month: 'Jun',
  shiraz: 26,
  cabernet: 18,
  pinotNoir: 15,
  chardonnay: 12,
  sauvBlanc: 11,
  rose: 8,
  sparkling: 5,
  other: 5
}, {
  month: 'Jul',
  shiraz: 25,
  cabernet: 17,
  pinotNoir: 16,
  chardonnay: 13,
  sauvBlanc: 11,
  rose: 8,
  sparkling: 5,
  other: 5
}, {
  month: 'Aug',
  shiraz: 25,
  cabernet: 17,
  pinotNoir: 16,
  chardonnay: 13,
  sauvBlanc: 11,
  rose: 8,
  sparkling: 5,
  other: 5
}, {
  month: 'Sep',
  shiraz: 24,
  cabernet: 16,
  pinotNoir: 16,
  chardonnay: 14,
  sauvBlanc: 12,
  rose: 8,
  sparkling: 5,
  other: 5
}, {
  month: 'Oct',
  shiraz: 23,
  cabernet: 16,
  pinotNoir: 17,
  chardonnay: 14,
  sauvBlanc: 12,
  rose: 8,
  sparkling: 5,
  other: 5
}, {
  month: 'Nov',
  shiraz: 22,
  cabernet: 15,
  pinotNoir: 17,
  chardonnay: 14,
  sauvBlanc: 13,
  rose: 9,
  sparkling: 5,
  other: 5
}, {
  month: 'Dec',
  shiraz: 22,
  cabernet: 15,
  pinotNoir: 17,
  chardonnay: 14,
  sauvBlanc: 13,
  rose: 9,
  sparkling: 5,
  other: 5
}];
export const revenueProducts: RevenueProduct[] = [{
  name: '2022 Shiraz',
  subscriptionCount: 2768,
  baseForecastUnits: 3200,
  baseForecastRevenue: 96000,
  avgRecurring: 67.61,
  avgLTV: 90.15
}, {
  name: '2024 Grenache',
  subscriptionCount: 2520,
  baseForecastUnits: 2800,
  baseForecastRevenue: 70000,
  avgRecurring: 50.31,
  avgLTV: 72.69
}, {
  name: '2025 Fiano',
  subscriptionCount: 2243,
  baseForecastUnits: 2500,
  baseForecastRevenue: 62500,
  avgRecurring: 50.6,
  avgLTV: 59.45
}, {
  name: "2024 Brioni's Blend",
  subscriptionCount: 1744,
  baseForecastUnits: 1900,
  baseForecastRevenue: 47500,
  avgRecurring: 50.68,
  avgLTV: 87.34
}, {
  name: '2025 Vermentino',
  subscriptionCount: 1545,
  baseForecastUnits: 1700,
  baseForecastRevenue: 42500,
  avgRecurring: 42.56,
  avgLTV: 27.33
}, {
  name: '2025 Chica Mencia Rosé',
  subscriptionCount: 1338,
  baseForecastUnits: 1500,
  baseForecastRevenue: 37500,
  avgRecurring: 43.97,
  avgLTV: 22.87
}, {
  name: '2021 HJ Shiraz',
  subscriptionCount: 1056,
  baseForecastUnits: 1100,
  baseForecastRevenue: 27500,
  avgRecurring: 128.82,
  avgLTV: 216.45
}, {
  name: '2024 Tempranillo',
  subscriptionCount: 1008,
  baseForecastUnits: 1100,
  baseForecastRevenue: 27500,
  avgRecurring: 43.73,
  avgLTV: 68.08
}];
// Sales Data
export type SalesMetric = 'Net' | 'Gross' | 'Refunds';
export interface DailySalesRow {
  date: string;
  orders: number;
  gross: number;
  refunds: number;
}
// Daily sales, oldest first. Net is derived as gross minus refunds.
export const dailySales: DailySalesRow[] = [{
  date: '2026-08-04',
  orders: 14,
  gross: 1840,
  refunds: 0
}, {
  date: '2026-08-05',
  orders: 11,
  gross: 1495.5,
  refunds: 0
}, {
  date: '2026-08-06',
  orders: 18,
  gross: 2380,
  refunds: 142
}, {
  date: '2026-08-07',
  orders: 22,
  gross: 2940.75,
  refunds: 0
}, {
  date: '2026-08-08',
  orders: 27,
  gross: 3610,
  refunds: 0
}, {
  date: '2026-08-09',
  orders: 16,
  gross: 2120.4,
  refunds: 96.5
}, {
  date: '2026-08-10',
  orders: 9,
  gross: 1265,
  refunds: 0
}, {
  date: '2026-08-11',
  orders: 13,
  gross: 1720.25,
  refunds: 0
}, {
  date: '2026-08-12',
  orders: 20,
  gross: 2655,
  refunds: 188
}, {
  date: '2026-08-13',
  orders: 24,
  gross: 3180.5,
  refunds: 0
}, {
  date: '2026-08-14',
  orders: 30,
  gross: 4020,
  refunds: 210.75
}, {
  date: '2026-08-15',
  orders: 19,
  gross: 2510,
  refunds: 0
}, {
  date: '2026-08-16',
  orders: 12,
  gross: 1580.6,
  refunds: 0
}, {
  date: '2026-08-17',
  orders: 15,
  gross: 1975,
  refunds: 88
}, {
  date: '2026-08-18',
  orders: 21,
  gross: 2790.25,
  refunds: 0
}, {
  date: '2026-08-19',
  orders: 26,
  gross: 3455,
  refunds: 0
}, {
  date: '2026-08-20',
  orders: 34,
  gross: 5240,
  refunds: 320
}, {
  date: '2026-08-21',
  orders: 17,
  gross: 2245.8,
  refunds: 0
}, {
  date: '2026-08-22',
  orders: 10,
  gross: 1330,
  refunds: 0
}, {
  date: '2026-08-23',
  orders: 14,
  gross: 1860,
  refunds: 74.5
}, {
  date: '2026-08-24',
  orders: 23,
  gross: 3050.4,
  refunds: 0
}, {
  date: '2026-08-25',
  orders: 28,
  gross: 3720,
  refunds: 165.25
}, {
  date: '2026-08-26',
  orders: 20,
  gross: 2640,
  refunds: 0
}, {
  date: '2026-08-27',
  orders: 12,
  gross: 1590,
  refunds: 0
}, {
  date: '2026-08-28',
  orders: 16,
  gross: 2115.75,
  refunds: 102
}, {
  date: '2026-08-29',
  orders: 25,
  gross: 3310,
  refunds: 0
}, {
  date: '2026-08-30',
  orders: 31,
  gross: 4680,
  refunds: 245.5
}, {
  date: '2026-08-31',
  orders: 18,
  gross: 2385,
  refunds: 0
}, {
  date: '2026-09-01',
  orders: 13,
  gross: 1725.6,
  refunds: 68
}, {
  date: '2026-09-02',
  orders: 21,
  gross: 2795,
  refunds: 0
}];
// All sales (subscription plus one-off orders), oldest first, same dates.
export const allDailySales: DailySalesRow[] = [{
  date: '2026-08-04',
  orders: 36,
  gross: 4620,
  refunds: 0
}, {
  date: '2026-08-05',
  orders: 28,
  gross: 3740.25,
  refunds: 0
}, {
  date: '2026-08-06',
  orders: 45,
  gross: 5950,
  refunds: 362
}, {
  date: '2026-08-07',
  orders: 56,
  gross: 7350.5,
  refunds: 0
}, {
  date: '2026-08-08',
  orders: 68,
  gross: 9025,
  refunds: 0
}, {
  date: '2026-08-09',
  orders: 41,
  gross: 5300.8,
  refunds: 241.25
}, {
  date: '2026-08-10',
  orders: 23,
  gross: 3162.5,
  refunds: 0
}, {
  date: '2026-08-11',
  orders: 33,
  gross: 4300.75,
  refunds: 0
}, {
  date: '2026-08-12',
  orders: 51,
  gross: 6637.5,
  refunds: 470
}, {
  date: '2026-08-13',
  orders: 60,
  gross: 7951.25,
  refunds: 0
}, {
  date: '2026-08-14',
  orders: 76,
  gross: 10050,
  refunds: 526.5
}, {
  date: '2026-08-15',
  orders: 48,
  gross: 6275,
  refunds: 0
}, {
  date: '2026-08-16',
  orders: 30,
  gross: 3951.5,
  refunds: 0
}, {
  date: '2026-08-17',
  orders: 38,
  gross: 4937.5,
  refunds: 220
}, {
  date: '2026-08-18',
  orders: 53,
  gross: 6975.6,
  refunds: 0
}, {
  date: '2026-08-19',
  orders: 65,
  gross: 8637.5,
  refunds: 0
}, {
  date: '2026-08-20',
  orders: 85,
  gross: 13100,
  refunds: 800
}, {
  date: '2026-08-21',
  orders: 43,
  gross: 5614.5,
  refunds: 0
}, {
  date: '2026-08-22',
  orders: 25,
  gross: 3325,
  refunds: 0
}, {
  date: '2026-08-23',
  orders: 35,
  gross: 4650,
  refunds: 186.25
}, {
  date: '2026-08-24',
  orders: 58,
  gross: 7626,
  refunds: 0
}, {
  date: '2026-08-25',
  orders: 70,
  gross: 9300,
  refunds: 413
}, {
  date: '2026-08-26',
  orders: 50,
  gross: 6600,
  refunds: 0
}, {
  date: '2026-08-27',
  orders: 30,
  gross: 3975,
  refunds: 0
}, {
  date: '2026-08-28',
  orders: 40,
  gross: 5289.4,
  refunds: 255
}, {
  date: '2026-08-29',
  orders: 63,
  gross: 8275,
  refunds: 0
}, {
  date: '2026-08-30',
  orders: 78,
  gross: 11700,
  refunds: 613.75
}, {
  date: '2026-08-31',
  orders: 45,
  gross: 5962.5,
  refunds: 0
}, {
  date: '2026-09-01',
  orders: 33,
  gross: 4314,
  refunds: 170
}, {
  date: '2026-09-02',
  orders: 53,
  gross: 6987.5,
  refunds: 0
}];
export const salesRangeDays: Record<string, number> = {
  'Last 7 Days': 7,
  'Last 30 Days': 30,
  'Last 90 Days': 30,
  'This Year': 30,
  Custom: 30
};
export const formatSalesMoney = (amount: number): string => '$' + amount.toLocaleString('en-US', {
  minimumFractionDigits: 2,
  maximumFractionDigits: 2
});
export const formatSalesDay = (date: string): string => {
  const parsed = new Date(date);
  return parsed.toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric'
  });
};
